import React from 'react';
import { formatDuration, formatTime, formatDate, formatDateTimeLocal } from '../utils/formatters';

export function TimeEntry({
  entry,
  project,
  projects,
  currentTime,
  isEditing,
  editForm,
  onEditFormChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onUpdateTimer,
  onUpdateTimerStartTime,
  showDate = false
}) {
  const isRunning = !entry.endTime;
  const duration = isRunning ? currentTime - entry.startTime : entry.endTime - entry.startTime;

  if (isEditing) {
    return (
      <div className="bg-white border-2 border-blue-500 rounded-lg p-4">
        <div className="space-y-3">
          <input
            type="text"
            value={editForm.description}
            onChange={(e) => onEditFormChange({ ...editForm, description: e.target.value })}
            placeholder="What are you working on?"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={editForm.projectId}
            onChange={(e) => onEditFormChange({ ...editForm, projectId: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {projects.map(proj => (
              <option key={proj.id} value={proj.id}>{proj.name}</option>
            ))}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Start Time</label>
              <input
                type="datetime-local"
                value={editForm.startTime}
                onChange={(e) => onEditFormChange({ ...editForm, startTime: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">End Time</label>
              <input
                type="datetime-local"
                value={editForm.endTime}
                onChange={(e) => onEditFormChange({ ...editForm, endTime: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onSaveEdit}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Save
            </button>
            <button
              onClick={onCancelEdit}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isRunning) {
    return (
      <div
        className="bg-white border-2 border-blue-500 shadow-lg rounded-lg p-4"
        style={{ borderLeftWidth: '4px', borderLeftColor: project.color }}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="space-y-2">
              <div className="text-sm text-blue-600 font-medium">RUNNING</div>
              <input
                type="text"
                value={entry.description}
                onChange={(e) => onUpdateTimer(e.target.value)}
                placeholder="What are you working on?"
                className="w-full font-medium text-gray-900 bg-white border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={entry.projectId}
                onChange={(e) => onUpdateTimer(entry.description, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                {projects.map(proj => (
                  <option key={proj.id} value={proj.id}>{proj.name}</option>
                ))}
              </select>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>Started:</span>
                <input
                  type="datetime-local"
                  value={formatDateTimeLocal(entry.startTime)}
                  onChange={(e) => onUpdateTimerStartTime(e.target.value)}
                  max={formatDateTimeLocal(Date.now())}
                  className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 ml-4">
            <div className="text-lg font-semibold text-blue-600">
              {formatDuration(duration)}
            </div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-4"
      style={{ borderLeftWidth: '4px', borderLeftColor: project.color }}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="font-medium text-gray-900">
            {entry.description || <span className="text-gray-400 italic">No description</span>}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span
              className="text-xs px-2 py-1 rounded-full text-white font-medium"
              style={{ backgroundColor: project.color }}
            >
              {project.name}
            </span>
            <span className="text-sm text-gray-500">
              {showDate && `${formatDate(entry.startTime)} • `}
              {formatTime(entry.startTime)} - {formatTime(entry.endTime)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-lg font-semibold text-gray-900">
            {formatDuration(duration)}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onStartEdit(entry)}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Edit"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(entry.id);
              }}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
